import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useLang, canSeePrices } from "@/contexts/LanguageContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { Lock } from "lucide-react";

const CAT_KEYS = [
  { slug: "all", labelKey: "catalog.filter.all" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t } = useLang();
  const { categories } = useCategories();
  const showPrices = canSeePrices(user);

  // Merge dynamic categories with the "All" option
  const filters = useMemo(
    () => [CAT_KEYS[0], ...categories.map((c) => ({ slug: c.slug, label: c.title }))],
    [categories]
  );

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products${category && category !== "all" ? `?category=${category}` : ""}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => products, [products]);

  const lockedLabel =
    !user || user === false
      ? t("catalog.priceLocked")
      : t("catalog.priceLockedVerified");

  let listView;
  if (loading) {
    listView = (
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
        {["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"].map((k) => (
          <div key={k} className="border border-border aspect-[3/4] bg-white animate-pulse" />
        ))}
      </div>
    );
  } else if (sorted.length === 0) {
    listView = (
      <div className="border border-border bg-white p-12 text-center text-sm text-muted-foreground">
        {t("catalog.empty")}
      </div>
    );
  } else {
    listView = (
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
        {sorted.map((p) => (
          <Link
            key={p.product_id}
            to={`/product/${p.slug}`}
            data-testid={`product-card-${p.slug}`}
            className="group border border-border bg-white lift overflow-hidden flex flex-col"
          >
            <div className="aspect-square overflow-hidden bg-[hsl(var(--secondary))]">
              <img
                src={resolveImageUrl(p.image_url)}
                alt={p.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <ProductCardBody p={p} showPrices={showPrices} lockedLabel={lockedLabel} />
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="catalog-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 lg:mb-10">
        <div>
          <div className="overline mb-2">{t("catalog.overline")}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter">
            {t("catalog.title")}
          </h1>
          <p className="text-sm text-muted-foreground max-w-lg mt-3">
            {t("catalog.subtitle")}
          </p>
        </div>

        {/* Horizontal-scroll filter pills on mobile, flex-wrap on desktop */}
        <div
          className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap pb-1 no-scrollbar"
          data-testid="catalog-filters"
        >
          {filters.map((c) => {
            const active = category === c.slug;
            const label = c.labelKey ? t(c.labelKey) : c.label;
            return (
              <button
                key={c.slug}
                data-testid={`filter-${c.slug}`}
                onClick={() => setParams(c.slug === "all" ? {} : { category: c.slug })}
                className={`shrink-0 px-4 h-9 text-sm rounded-full border transition-colors ${
                  active
                    ? "bg-foreground text-white border-foreground"
                    : "bg-white text-muted-foreground border-border hover:text-foreground"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {listView}
    </div>
  );
}

function ProductCardBody({ p, showPrices, lockedLabel }) {
  const { t } = useLang();
  return (
    <div className="p-3 sm:p-5 flex-1 flex flex-col">
      <div className="overline text-[9px] sm:text-xs">{p.category}</div>
      <h3 className="font-display text-sm sm:text-lg font-semibold tracking-tight mt-1 line-clamp-2">
        {p.name}
      </h3>
      <p className="hidden sm:block text-xs text-muted-foreground mt-1 line-clamp-2">{p.short_desc}</p>
      <div className="thin-rule my-3 sm:my-4" />
      <div className="flex items-center justify-between gap-1">
        {showPrices ? (
          <span className="font-mono text-sm" data-testid={`price-${p.slug}`}>
            ${p.price.toFixed(2)}
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 font-mono text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground"
            data-testid={`price-locked-${p.slug}`}
          >
            <Lock className="w-3 h-3" />
            <span className="truncate">{lockedLabel}</span>
          </span>
        )}
        <Button size="sm" variant="ghost" className="rounded-md text-xs px-2 shrink-0 hidden sm:inline-flex">
          {t("catalog.details")}
        </Button>
      </div>
    </div>
  );
}
